import { useCallback, useEffect, useRef, useState, type FC } from "react";
import { Button, ListItem, Menu, Stack } from "~/common/mui";
import SettingsIcon from "@mui/icons-material/Settings";
import { ColumnConfigurator } from "./ColumnConfigurator";
import type { ColumnData } from "./ColumnConfigurator";

type PersistFn = (cols: ColumnData[], isReset: boolean) => void;

interface ConfigureColumnsPopoverProps {
  /** Text shown on the button */
  buttonLabel: string;
  /** Initial column list – could come from props or Redux */
  initialColumns: ColumnData[];
  /** Called when the user clicks “Save” */
  onSave: PersistFn;
  /** Called when the user clicks “Reset” */
  onReset: PersistFn;
  /** Optional: translation function for column headers */
  headerT?: (key: string) => string;
}

/** A reusable pop‑over that handles drafting, moving & visibility toggling */
export const ConfigureColumnsPopover: FC<ConfigureColumnsPopoverProps> = ({
  buttonLabel,
  initialColumns,
  onSave,
  onReset,
  headerT,
}) => {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLButtonElement>(null);
  const [draft, setDraft] = useState<ColumnData[]>(initialColumns);

  // reset draft each time the pop‑over opens
  useEffect(() => {
    if (open) {
      setDraft(initialColumns);
    }
  }, [open, initialColumns]);

  const moveColumn = useCallback((oldIdx: number, newIdx: number) => {
    setDraft((prev) => {
      const next = [...prev];
      const [item] = next.splice(oldIdx, 1);
      next.splice(newIdx, 0, item);
      return next;
    });
  }, []);

  const toggleVisibility = useCallback((idx: number, visible: boolean) => {
    setDraft((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], visible };
      return next;
    });
  }, []);

  const handleReset = () => {
    // let the consumer decide what “reset” means
    const resetCols = onReset(draft, true);
    // If the consumer returns a fresh list we can replace the draft,
    // otherwise just close the menu.
    if (Array.isArray(resetCols)) {
      setDraft(resetCols);
    }
    setOpen(false);
  };

  const handleSave = () => {
    onSave(draft, false);
    setOpen(false);
  };

  return (
    <>
      <Button
        variant="text"
        onClick={() => setOpen(true)}
        ref={anchorRef}
        startIcon={<SettingsIcon />}
      >
        {buttonLabel}
      </Button>

      <Menu
        open={open}
        onClose={() => setOpen(false)}
        anchorEl={anchorRef.current}
        slotProps={{
          list: {
            sx: { gap: "8px", display: "flex", flexDirection: "column" },
          },
        }}
      >
        <ColumnConfigurator
          items={draft}
          moveColumn={moveColumn}
          updateVisibility={toggleVisibility}
        />

        <ListItem component={Stack} direction="row" spacing={2}>
          <Button fullWidth variant="text" onClick={handleReset}>
            Reset
          </Button>
          <Button fullWidth onClick={handleSave}>
            Save
          </Button>
        </ListItem>
      </Menu>
    </>
  );
};
